import { useEffect, useRef, useState } from "react";
import { fetchData } from "../../../api/DataApi";
import { useSearchParams } from "react-router-dom";
import TranslationRequestRepository from "../../../repository/TranslationRequestRepository";
import { CircularProgress, Dialog } from "@mui/material";
import { fetchStepInput } from "../../../api/StepInputApi";
import { StepInput } from "../../../model/StepInput";
import { ExternalLink, Maximize2 } from "lucide-react";
import TranslationNavBarComponent from "../../generic/TranslationNavBarComponent";

interface TranslationWeb {
    i_pk: string;
    i_rk: string;
    si_pk: string;
    si_rk: string;
}

interface TranslationRequest {
    partition_key: string;
    range_key: string;
    title: string;
    description: string;
    type: string;
    files: string;
    estimated_cost: number;
    user_locale: string;
    currency: string;
    status: string;
    created: string;
    modified: string;
    user_id: string;
    assigned_user_id: string;
    translation_quote: string;
    payment_id: string;
}

interface PreviewContent {
    image: string;
    html: string;
}

const TranslationsMyRequestDetailScreen = () => {
    const [files, setFiles] = useState<TranslationWeb[]>([]);
    const [inputs, setInputs] = useState<{ [key: string]: StepInput }>({});
    const initialized = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const [translationRequest, setTranslationRequest] = useState<TranslationRequest | null>(null);
    const partitionKey = searchParams.get("partitionKey");
    const rangeKey = searchParams.get("rangeKey");
    const [images, setImages] = useState<{ [key: string]: string }>({});
    const [selectedPreview, setSelectedPreview] = useState<PreviewContent | null>(null);

    const extractHtmlFromMarkdown = (input: string): string => {
        const match = input.match(/```html\n([\s\S]*?)\n```/);
        return match ? match[1] : '';
    };

    const fetchTranslation = async () => {
        setIsLoading(true);
        try {
            let translationRequest = await TranslationRequestRepository.fetchTranslation(partitionKey!!, rangeKey!!);
            setTranslationRequest(translationRequest);
            if (translationRequest.type === "image" && translationRequest.completed_steps.length > 0) {
                let files: TranslationWeb[] = [];
                let dupes = JSON.parse(translationRequest.completed_steps) as TranslationWeb[];
                dupes.forEach((item) => {
                    let index = files.findIndex((file) => file.i_rk === item.i_rk);
                    if (index === -1) {
                        files.push(item);
                    }
                });
                setFiles(files);
                fetchAllImages(files);
                fetchAllStepInput(files);
            }
        } catch (error) {
            console.error("Error fetching translation:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllImages = async (files: TranslationWeb[]) => {
        const imagePromises = files.map(async (file) => {
            const data = await fetchData(file.i_pk, file.i_rk);
            return { key: file.i_rk, url: data.body };
        });

        const resolvedImages = await Promise.all(imagePromises);
        const imageMap = resolvedImages.reduce((acc, { key, url }) => {
            acc[key] = url;
            return acc;
        }, {} as { [key: string]: string });

        setImages(imageMap);
    };

    const fetchAllStepInput = async (files: TranslationWeb[]) => {
        const inputPromises = files.map(async (file) => {
            const data = await fetchStepInput(file.si_pk, file.si_rk);
            return { key: file.si_rk, data };
        });

        const resolvedInputs = await Promise.all(inputPromises);
        const inputMap = resolvedInputs.reduce((acc, { key, data }) => {
            acc[key] = data;
            return acc;
        }, {} as { [key: string]: StepInput });

        setInputs(inputMap);
    };

    useEffect(() => {
        var isSubscribed = true;
        if (!initialized.current && isSubscribed) {
            initialized.current = true;
            fetchTranslation();
        }
        return () => {
            isSubscribed = false;
        };
    }, []);

    return (
        <div>
            <TranslationNavBarComponent />
            <main>
                <div className="p-4">
                    {isLoading && (
                        <div className="flex justify-center">
                            <CircularProgress />
                        </div>
                    )}
                    {translationRequest && (
                        <div className="space-y-4">
                            <h1 className="text-2xl font-bold">{translationRequest.title}</h1>
                            <p className="text-gray-700">{translationRequest.description}</p>
                            <p className="text-lg">
                                Cost: {translationRequest.estimated_cost.toFixed(2)} {translationRequest.currency}
                            </p>

                            <div className="overflow-x-auto">
                                <table className="min-w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="border p-3 text-left">#</th>
                                            <th className="border p-3 text-left">Content Preview</th>
                                            <th className="border p-3 text-left">HTML</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {files.map((file, index) => {
                                            const htmlContent = inputs[file.si_rk] ?
                                                extractHtmlFromMarkdown(inputs[file.si_rk].input) : '';

                                            return (
                                                <tr key={file.i_rk} className="hover:bg-gray-50">
                                                    <td className="border p-3 text-gray-600 w-12">
                                                        {index + 1}
                                                    </td>
                                                    <td className="border p-3">
                                                        <div className="flex gap-4 items-start relative group cursor-pointer"
                                                            onClick={() => setSelectedPreview({
                                                                image: images[file.i_rk],
                                                                html: htmlContent
                                                            })}>
                                                            {images[file.i_rk] && (
                                                                <div className="relative">
                                                                    <img
                                                                        src={images[file.i_rk]}
                                                                        alt={`Translation ${index + 1}`}
                                                                        width={300}
                                                                        className="w-40 h-40 object-cover rounded"
                                                                    />
                                                                </div>
                                                            )}

                                                        </div>
                                                    </td>
                                                    <td className="border p-3">
                                                        {htmlContent && (
                                                            <div className="w-96 h-40 overflow-hidden border rounded p-2 bg-white">
                                                                <div
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: htmlContent
                                                                    }}
                                                                    className="prose max-w-none"
                                                                />
                                                            </div>
                                                        )}
                                                        <button
                                                            className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <Maximize2 className="w-4 h-4 text-gray-600" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Combined Preview Dialog */}
                    <Dialog
                        open={!!selectedPreview}
                        onClose={() => setSelectedPreview(null)}
                        maxWidth="lg"
                        fullWidth
                    >
                        {selectedPreview && (
                            <div className="p-6">
                                <table>
                                    <td><img
                                        src={selectedPreview.image}
                                        alt="Translation Preview"
                                        className="w-full h-auto rounded"
                                    />
                                    </td>
                                    <td>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: selectedPreview.html
                                            }}
                                            className="prose max-w-none"
                                        />

                                    </td>
                                </table>
                                <div className="flex gap-6">
                                    <div className="w-1/2">

                                    </div>

                                </div>
                            </div>
                        )}
                    </Dialog>
                </div>
            </main>
        </div>

    );
};

export default TranslationsMyRequestDetailScreen;